import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { User, Briefcase, FileText, Edit2, Save, X, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { employeeService, languageService, positionService, partyContactService, organizationService } from '../../api/services';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';
import {
  DGMA_DISABILITY_OPTIONS,
  DGMA_SEXUAL_ORIENTATIONS_OPTIONS,
  DGMA_RELIGION_OPTIONS,
  DGMA_TRANSGENDER_OPTIONS
} from '../../utils/formConstants';

// MultiSelect Component
const MultiSelect = ({ label, options, value = [], onChange, disabled = false, fieldName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  const toggleOption = (option) => {
    if (disabled) return;

    const newValues = selectedValues.includes(option)
      ? selectedValues.filter(v => v !== option)
      : [...selectedValues, option];

    onChange({ target: { name: fieldName, value: newValues } });
  };

  return (
    <div className="relative">
      <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      <div className="relative">
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`w-full px-2 py-2 text-sm border rounded min-h-[38px] flex flex-wrap gap-1 ${
            disabled ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'bg-white cursor-pointer border-gray-300 hover:border-indigo-400'
          }`}
        >
          {selectedValues.length === 0 ? (
            <span className="text-gray-400 text-sm">Select {label}</span>
          ) : (
            selectedValues.map((val, idx) => (
              <span key={idx} className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs flex items-center gap-1">
                {val}
                {!disabled && (
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-indigo-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(val);
                    }}
                  />
                )}
              </span>
            ))
          )}
        </div>
        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
            {options.map((option, idx) => (
              <div
                key={idx}
                onClick={() => toggleOption(option)}
                className={`px-3 py-2 cursor-pointer text-sm hover:bg-indigo-50 flex items-center gap-2 ${
                  selectedValues.includes(option) ? 'bg-indigo-100' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() => {}}
                  className="cursor-pointer"
                />
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
      {isOpen && !disabled && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Personal Details Component with sections
const PersonalDetailsView = ({ employeeData, languagesData = [], bankAccountData = null, onBankAccountUpdate, personalContactData = [], onPersonalContactUpdate, postalAddressesMap = {}, partyContactsData = [], contactPartyContactsMap = {}, isLoadingContactAddresses = false, countryOptions = [], onUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const userProfile = useEmployeeProfile();

  if (!employeeData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="ml-3 text-gray-600">Loading personal details...</p>
        </div>
      </div>
    );
  }

  const handleEditClick = () => {
    // Only include fields that exist in the EmployeesV2 API
    setEditValues({
      // Basic Information
      KnownAs: employeeData.KnownAs || '',
      DGMA_PersonalTitle: employeeData.DGMA_PersonalTitle || '',
      // Address (valid API fields only)
      AddressNameDescription: employeeData.AddressNameDescription || '',
      AddressStreet: employeeData.AddressStreet || '',
      AddressCity: employeeData.AddressCity || '',

      AddressCountryRegionId: employeeData.AddressCountryRegionId || '',
      AddressZipCode: employeeData.AddressZipCode || '',

      // Contact Information & Protected Characteristics
      DGMA_EthnicOrigin: employeeData.DGMA_EthnicOrigin || '',
      SHPSex: employeeData.SHPSex || '',
      DGMA_Disability: employeeData.DGMA_Disability || '',
      DGMA_SexualOrientations: employeeData.DGMA_SexualOrientations || '',
      DGMA_Religion: employeeData.DGMA_Religion || '',
      DGMA_Transgender: employeeData.DGMA_Transgender || '',
      // Bank Accounts (from WorkerBankAccounts API)
      BankAccountType: bankAccountData?.AccountIdentification || bankAccountData?.BankAccountType || '',
      Name: bankAccountData?.Name || '',
      BankAccountNumber: bankAccountData?.BankAccountNumber || '',
      AccountHolder: bankAccountData?.AccountHolder || '',
      BranchNumber: bankAccountData?.BranchNumber || '',
      DGMA_BankRollNumber: bankAccountData?.DGMA_BankRollNumber || '',
      // Postal Address fields are now managed per-contact via postalAddressesMap
      // Personal Contacts (from PersonalContactPeople API)
      PersonalContacts: personalContactData.length > 0 ? personalContactData.map((c, idx) => ({
        contactPersonPartyNumber: c.ContactPersonPartyNumber,
        firstName: c.ContactFirstName || '',
        middleName: c.ContactMiddleName || '',
        lastName: c.ContactLastName || '',
        relationship: c.DGMA_RelationShipType || '',
        emergencyContactInfo: c.IsEmergencyContact || '',
        primarySecondaryPhone: c.EmergencyContactPrimarySecondary || '',
        addresses: (() => {
          const addrData = postalAddressesMap[c.ContactPersonPartyNumber];
          return addrData
            ? [{
                name: addrData.Description || '',
                street: addrData.Street || '',
                city: addrData.City || '',
                country: addrData.CountryRegionId || '',
                zipCode: addrData.ZipCode || '',
                contactNumber: contactPartyContactsMap[c.ContactPersonPartyNumber]?.Locator || '',
                type: addrData.Roles || '',
              }]
            : [{ name: '', street: '', city: '', country: '', zipCode: '', contactNumber: contactPartyContactsMap[c.ContactPersonPartyNumber]?.Locator || '', type: '' }];
        })(),
      })) : [],
    });
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditValues({});
    setSaveMessage(null);
  };

  const handleSave = async () => {
    if (!userProfile?.employeeNo) {
      setSaveMessage({ type: 'error', text: 'Employee number not available' });
      return;
    }

    // Validate Bank Account Number (must be 8 digits)
    if (editValues.BankAccountNumber && !/^\d{8}$/.test(editValues.BankAccountNumber)) {
      setSaveMessage({ type: 'error', text: 'Bank Account Number must be exactly 8 digits' });
      return;
    }

    // Validate Sort Code (must be 6 digits)
    if (editValues.BranchNumber && !/^\d{6}$/.test(editValues.BranchNumber)) {
      setSaveMessage({ type: 'error', text: 'Sort Code must be exactly 6 digits' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      // Bank account fields (handled via WorkerBankAccounts API separately)
      const bankFields = ['BankAccountType', 'Name', 'BankAccountNumber', 'AccountHolder', 'BranchNumber', 'DGMA_BankRollNumber'];

      // Employee (EmployeesV2) editable fields — must match handleEditClick
      const employeeFields = [
        'KnownAs', 'DGMA_PersonalTitle',
        'AddressNameDescription', 'AddressStreet', 'AddressCity', 'AddressCountryRegionId', 'AddressZipCode',
        'DGMA_EthnicOrigin', 'SHPSex', 'DGMA_Disability', 'DGMA_SexualOrientations', 'DGMA_Religion', 'DGMA_Transgender',
      ];

      // Only include employee fields that actually changed
      const changedEmployeeData = {};
      employeeFields.forEach(f => {
        if ((editValues[f] ?? '') !== (employeeData[f] ?? '')) {
          changedEmployeeData[f] = editValues[f];
        }
      });

      if (Object.keys(changedEmployeeData).length > 0) {
        await employeeService.updateEmployee(userProfile.employeeNo, {
          ...changedEmployeeData,
          EmploymentLegalEntityId: employeeData.EmploymentLegalEntityId || 'USMF',
          EmploymentId: employeeData.EmploymentId
        });
      }

      // Only patch WorkerBankAccounts if any editable bank field changed.
      // Compare against the same source used to initialise editValues (see handleEditClick),
      // and exclude BankAccountType (read-only key, mapped from AccountIdentification).
      if (bankAccountData?.AccountIdentification) {
        const editableBankFields = bankFields.filter(f => f !== 'BankAccountType');
        const changedBankData = {};
        editableBankFields.forEach(f => {
          if ((editValues[f] || '') !== (bankAccountData?.[f] || '')) {
            changedBankData[f] = editValues[f];
          }
        });

        if (Object.keys(changedBankData).length > 0) {
          await employeeService.updateWorkerBankAccount(
            userProfile.employeeNo,
            bankAccountData.AccountIdentification,
            changedBankData
          );
          if (onBankAccountUpdate) {
            onBankAccountUpdate({ ...bankAccountData, ...changedBankData });
          }
        }
      }

      // Patch or create postal addresses for all existing contacts
      const addrFieldMap = {
        name: 'Description',
        street: 'Street',
        city: 'City',
        zipCode: 'ZipCode',
        country: 'CountryRegionId',
      };
      for (const contact of (editValues.PersonalContacts || [])) {
        if (!contact.contactPersonPartyNumber) continue;
        const postalAddressData = postalAddressesMap[contact.contactPersonPartyNumber];
        const editedAddr = contact.addresses?.[0];
        if (!editedAddr) continue;

        if (postalAddressData?.PartyNumber && postalAddressData?.LocationId) {
          // Update existing postal address
          const changedPostalData = {};
          Object.entries(addrFieldMap).forEach(([localKey, apiKey]) => {
            if ((editedAddr[localKey] || '') !== (postalAddressData[apiKey] || '')) {
              changedPostalData[apiKey] = editedAddr[localKey];
            }
          });
          if (Object.keys(changedPostalData).length > 0) {
            await employeeService.updatePartyLocationPostalAddress(
              postalAddressData.PartyNumber,
              postalAddressData.LocationId,
              postalAddressData.ValidFrom,
              changedPostalData
            );
          }
        } else if (editedAddr.street || editedAddr.city || editedAddr.name) {
          // Create new postal address for existing contact that doesn't have one
          await employeeService.createPartyLocationPostalAddress({
            PartyNumber: contact.contactPersonPartyNumber,
            Description: editedAddr.name || '',
            Street: editedAddr.street || '',
            City: editedAddr.city || '',
            CountryRegionId: editedAddr.country || '',
            ZipCode: editedAddr.zipCode || '',
            IsPrimary: 'Yes',
            Roles: 'Home',
            ValidFrom: new Date().toISOString(),
            ValidTo: '2154-12-31T23:59:59Z',
          });
        }
      }

      // PATCH existing contacts if changed, POST new contacts
      const contactFieldMap = {
        firstName: 'ContactFirstName',
        middleName: 'ContactMiddleName',
        lastName: 'ContactLastName',
        relationship: 'DGMA_RelationShipType',
        emergencyContactInfo: 'IsEmergencyContact',
        primarySecondaryPhone: 'EmergencyContactPrimarySecondary',
      };
      const updatedContacts = [...personalContactData];
      for (const contact of (editValues.PersonalContacts || [])) {
        if (contact.contactPersonPartyNumber) {
          // Existing contact - PATCH if changed
          const original = personalContactData.find(c => c.ContactPersonPartyNumber === contact.contactPersonPartyNumber);
          if (original) {
            const changedContactData = {};
            Object.entries(contactFieldMap).forEach(([localKey, apiKey]) => {
              if ((contact[localKey] || '') !== (original[apiKey] || '')) {
                changedContactData[apiKey] = contact[localKey];
              }
            });
            if (Object.keys(changedContactData).length > 0) {
              await employeeService.updatePersonalContactPerson(
                userProfile.employeeNo,
                contact.contactPersonPartyNumber,
                original.RelationshipType,
                original.RelationshipValidFrom,
                changedContactData
              );
              const idx = updatedContacts.findIndex(c => c.ContactPersonPartyNumber === contact.contactPersonPartyNumber);
              if (idx !== -1) updatedContacts[idx] = { ...updatedContacts[idx], ...changedContactData };
            }
          }
        }
      }

      // POST new contacts (no contactPersonPartyNumber)
      const newContacts = (editValues.PersonalContacts || []).filter(c => !c.contactPersonPartyNumber);
      let hadNewContacts = false;
      for (const contact of newContacts) {
        if (contact.firstName || contact.lastName) {
          hadNewContacts = true;
          const createRes = await employeeService.createPersonalContactPerson({
            WorkerPersonnelNumber: userProfile.employeeNo,
            ContactFirstName: contact.firstName || '',
            ContactMiddleName: contact.middleName || '',
            ContactLastName: contact.lastName || '',
            DGMA_RelationShipType: contact.relationship || '',
            EmergencyContactPrimarySecondary: contact.primarySecondaryPhone || '',
            RelationshipType: 'FamilyContact',
            RelationshipValidFrom: new Date().toISOString(),
            RelationshipValidTo: '2154-12-31T23:59:59Z',
            Gender: 'None',
            BirthDate: '1900-01-01T12:00:00Z',
            IsDependentDomesticPartnerVerified: 'No',
            IsDependent: 'No',
            DisabledVerificationDate: '1900-01-01T12:00:00Z',
            DependentCourtOrderedExpirationDate: '1900-01-01T12:00:00Z',
            IsBeneficiary: 'No',
            IsDependentCourtOrdered: 'No',
            IsDisabled: 'No',
            IsFulltimeStudent: 'No',
            ContactLastNamePrefix: '',
          });

          // Create postal addresses for this new contact using the returned PartyNumber
          const newPartyNumber = createRes?.data?.ContactPersonPartyNumber;
          if (newPartyNumber && (contact.addresses || []).length > 0) {
            for (const addr of contact.addresses) {
              if (addr.street || addr.city || addr.name) {
                await employeeService.createPartyLocationPostalAddress({
                  PartyNumber: newPartyNumber,
                  Description: addr.name || '',
                  Street: addr.street || '',
                  City: addr.city || '',
                  CountryRegionId: addr.country || '',
                  ZipCode: addr.zipCode || '',
                  IsPrimary: 'Yes',
                  Roles: 'Home',
                  ValidFrom: new Date().toISOString(),
                  ValidTo: '2154-12-31T23:59:59Z',
                });
              }

              // POST contact number to PartyContactsV3 for new contact
              if (newPartyNumber && addr.contactNumber) {
                await partyContactService.postPartyContact({
                  IsInstantMessage: 'No',
                  IsMobilePhone: 'No',
                  CountryRegionCode: '',
                  Purpose: 'Business',
                  Locator: addr.contactNumber,
                  LocatorExtension: '',
                  IsPrimary: contact.primarySecondaryPhone === 'Primary' ? 'Yes' : 'No',
                  Description: '',
                  Type: 'Phone',
                  IsPrivate: 'No',
                  PartyNumber: newPartyNumber,
                });
              }
            }
          }
        }
      }

      // PATCH PartyContactsV3 for existing contacts with a contact number
      for (const contact of (editValues.PersonalContacts || [])) {
        const contactNumber = contact.addresses?.[0]?.contactNumber;
        if (!contactNumber || !contact.contactPersonPartyNumber) continue;

        const existing = contactPartyContactsMap[contact.contactPersonPartyNumber];
        const isPrimary = contact.primarySecondaryPhone === 'Primary' ? 'Yes' : 'No';

        if (existing?.ElectronicAddressId) {
          // Only PATCH if the editable values (phone number / primary flag) actually changed
          const changedPartyData = {};
          if ((contactNumber || '') !== (existing.Locator || '')) changedPartyData.Locator = contactNumber;
          if (isPrimary !== (existing.IsPrimary || '')) changedPartyData.IsPrimary = isPrimary;

          if (Object.keys(changedPartyData).length > 0) {
            await partyContactService.patchPartyContact(contact.contactPersonPartyNumber, existing.ElectronicAddressId, changedPartyData);
          }
        } else {
          await partyContactService.postPartyContact({
            IsInstantMessage: 'No',
            IsMobilePhone: 'No',
            CountryRegionCode: '',
            Purpose: 'Business',
            Locator: contactNumber,
            LocatorExtension: '',
            IsPrimary: isPrimary,
            Description: '',
            Type: 'Phone',
            IsPrivate: 'No',
            PartyNumber: contact.contactPersonPartyNumber,
          });
        }
      }

      // Re-fetch contacts so newly created records get their API-assigned IDs
      if (hadNewContacts) {
        try {
          const freshRes = await employeeService.getPersonalContactPeople(userProfile.employeeNo);
          const freshContacts = freshRes.data?.value || [];
          if (onPersonalContactUpdate) onPersonalContactUpdate(freshContacts);
        } catch (e) {
          console.warn('Could not re-fetch contacts after create:', e);
          if (onPersonalContactUpdate) onPersonalContactUpdate(updatedContacts);
        }
      } else {
        if (onPersonalContactUpdate) onPersonalContactUpdate(updatedContacts);
      }

      if (onUpdate) {
        onUpdate({ ...employeeData, ...changedEmployeeData });
      }

      setIsEditMode(false);
      setSaveMessage({ type: 'success', text: 'All changes saved successfully' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('❌ Error updating personal information:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error response status:', error.response?.status);
      console.error('❌ Error message:', error.message);

      // Extract detailed error message
      let errorMessage = 'Failed to save changes. Please try again.';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.Message) {
        errorMessage = error.response.data.Message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSaveMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setSaveMessage(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  const handleContactChange = (contactIndex, field, value) => {
    const newContacts = [...(editValues.PersonalContacts || [])];
    newContacts[contactIndex] = { ...newContacts[contactIndex], [field]: value };
    setEditValues({ ...editValues, PersonalContacts: newContacts });
  };

  const handleContactAddressChange = (contactIndex, addressIndex, field, value) => {
    const newContacts = [...(editValues.PersonalContacts || [])];
    newContacts[contactIndex].addresses[addressIndex] = {
      ...newContacts[contactIndex].addresses[addressIndex],
      [field]: value
    };
    setEditValues({ ...editValues, PersonalContacts: newContacts });
  };

  const addContact = () => {
    setEditValues({
      ...editValues,
      PersonalContacts: [
        ...(editValues.PersonalContacts || []),
        {
          firstName: '',
          middleName: '',
          lastName: '',
          relationship: '',
          emergencyContactInfo: '',
          primarySecondaryPhone: '',
          addresses: [{ name: '', street: '', city: '', state: '', country: '', zipCode: '', contactNumber: '', type: '' }]
        }
      ]
    });
  };

  const removeContact = (index) => {
    const newContacts = (editValues.PersonalContacts || []).filter((_, i) => i !== index);
    setEditValues({ ...editValues, PersonalContacts: newContacts });
  };

  const addContactAddress = (contactIndex) => {
    const newContacts = [...(editValues.PersonalContacts || [])];
    newContacts[contactIndex].addresses.push({
      name: '', street: '', city: '', state: '', country: '', zipCode: '', contactNumber: '', type: ''
    });
    setEditValues({ ...editValues, PersonalContacts: newContacts });
  };

  const removeContactAddress = (contactIndex, addressIndex) => {
    const newContacts = [...(editValues.PersonalContacts || [])];
    newContacts[contactIndex].addresses = newContacts[contactIndex].addresses.filter((_, i) => i !== addressIndex);
    setEditValues({ ...editValues, PersonalContacts: newContacts });
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;

    // Local-only contact (not yet saved) — just remove from form
    if (!contactToDelete.isRemote) {
      const newContacts = (editValues.PersonalContacts || []).filter((_, i) => i !== contactToDelete.localIndex);
      setEditValues({ ...editValues, PersonalContacts: newContacts });
      setContactToDelete(null);
      return;
    }

    // Remote contact — call API
    if (!userProfile?.employeeNo) return;
    setDeleting(true);
    try {
      const { apiContact } = contactToDelete;
      // Delete all postal addresses for this contact's party number
      try {
        const postalRes = await employeeService.getPartyLocationPostalAddresses(apiContact.ContactPersonPartyNumber);
        const addresses = postalRes.data?.value || [];
        for (const addr of addresses) {
          await employeeService.deletePartyLocationPostalAddress(addr.PartyNumber, addr.LocationId, addr.ValidFrom);
        }
      } catch (e) {
        console.warn('Could not delete postal addresses for contact:', e);
      }
      // Delete PartyContactsV3 entry (phone number) for this contact
      try {
        const partyContactEntry = contactPartyContactsMap[apiContact.ContactPersonPartyNumber];
        if (partyContactEntry?.ElectronicAddressId) {
          await partyContactService.deletePartyContact(apiContact.ContactPersonPartyNumber, partyContactEntry.ElectronicAddressId);
        }
      } catch (e) {
        console.warn('Could not delete party contact for contact:', e);
      }
      // Delete the personal contact person
      await employeeService.deletePersonalContactPerson(
        userProfile.employeeNo,
        apiContact.ContactPersonPartyNumber,
        apiContact.RelationshipType,
        apiContact.RelationshipValidFrom,
      );
      const updated = (personalContactData ?? []).filter(
        c => c.ContactPersonPartyNumber !== apiContact.ContactPersonPartyNumber
      );
      if (onPersonalContactUpdate) onPersonalContactUpdate(updated);
      // Also remove from edit form if currently in edit mode
      if (isEditMode) {
        const newContacts = (editValues.PersonalContacts || []).filter(
          c => c.contactPersonPartyNumber !== apiContact.ContactPersonPartyNumber
        );
        setEditValues({ ...editValues, PersonalContacts: newContacts });
      }
      setContactToDelete(null);
      setSaveMessage({ type: 'success', text: 'Contact deleted successfully' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      const msg = error.response?.data?.error?.message || error.message || 'Failed to delete contact';
      setSaveMessage({ type: 'error', text: msg });
      setTimeout(() => setSaveMessage(null), 8000);
    } finally {
      setDeleting(false);
    }
  };

  const renderField = (label, value, editable = false, fieldName = '', type = 'text', options = null) => {
    // Map value to label for display when options are provided
    let displayValue = value || 'N/A';
    if (options && value && !Array.isArray(value)) {
      const matchedOption = options.find(opt => {
        const optValue = typeof opt === 'string' ? opt : opt.value;
        return optValue === value;
      });
      if (matchedOption) {
        displayValue = typeof matchedOption === 'string' ? matchedOption : matchedOption.label;
      }
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    }

    const isEditing = isEditMode && editable;

    return (
      <div className={`border border-gray-100 rounded-lg p-3 ${!editable && isEditMode ? 'bg-gray-50 opacity-60' : ''}`}>
        {isEditing && type === 'multiselect' && options ? (
          <MultiSelect
            label={label}
            options={options}
            value={editValues[fieldName]}
            onChange={(e) => setEditValues({ ...editValues, [fieldName]: e.target.value })}
            disabled={saving}
            fieldName={fieldName}
          />
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
            {isEditing ? (
              type === 'textarea' ? (
                <textarea
                  value={editValues[fieldName]}
                  onChange={(e) => setEditValues({ ...editValues, [fieldName]: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={saving}
                  rows={3}
                />
              ) : type === 'select' && options ? (
                <select
                  value={editValues[fieldName] || ''}
                  onChange={(e) => setEditValues({ ...editValues, [fieldName]: e.target.value })}
                  className="w-full px-2 py-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  disabled={saving}
                >
                  <option value="">Select {label}</option>
                  {options.map((option, idx) => {
                    // Support both string arrays and {value, label} object arrays
                    const optionValue = typeof option === 'string' ? option : option.value;
                    const optionLabel = typeof option === 'string' ? option : option.label;
                    return (
                      <option key={idx} value={optionValue}>{optionLabel}</option>
                    );
                  })}
                </select>
              ) : (
                <input
                  type={type}
                  value={editValues[fieldName]}
                  onChange={(e) => setEditValues({ ...editValues, [fieldName]: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={saving}
                />
              )
            ) : (
              <p className="text-sm text-gray-800 font-medium">{displayValue}</p>
            )}
          </>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1900-01-01T00:00:00Z' || dateStr === '1900-01-01T12:00:00Z') return 'N/A';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="p-6">
      {/* Personal Details Header with Edit/Save Buttons */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <User className="w-6 h-6 text-indigo-600" />
          Personal Details
        </h2>

        {/* Edit/Save Buttons */}
        <div className="flex gap-2">
          {!isEditMode ? (
            <button
              onClick={handleEditClick}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {saveMessage.type === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          <span className="text-sm">{saveMessage.text}</span>
        </div>
      )}

      {/* Basic Information Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('First Name', employeeData.FirstName, false)}
          {renderField('Middle Name', employeeData.MiddleName, false)}
          {renderField('Last Name', employeeData.LastName, false)}
          {renderField('Known As', employeeData.KnownAs, true, 'KnownAs')}
          {renderField('Title', employeeData.DGMA_PersonalTitle, true, 'DGMA_PersonalTitle')}
        </div>
      </div>

      {/* Worker Summary Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Worker Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Personnel Number', employeeData.PersonnelNumber, false)}
          {renderField('Original Hire Date', formatDate(employeeData.OriginalHireDateTime), false)}
          {renderField('Office Location', employeeData.OfficeLocation, false)}
          {renderField('Office Address', employeeData.DGMA_OfficeAddress, false)}
        </div>
      </div>

      {/* Address Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Address Name', employeeData.AddressNameDescription, true, 'AddressNameDescription')}
          {renderField('Street', employeeData.AddressStreet, true, 'AddressStreet')}
          {renderField('City', employeeData.AddressCity, true, 'AddressCity')}

          {renderField('Country', employeeData.AddressCountryRegionId, true, 'AddressCountryRegionId', 'select', countryOptions)}
          {renderField('Post Code', employeeData.AddressZipCode, true, 'AddressZipCode')}
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Contact Number / Address', employeeData.PrimaryContactPhone, false)}
          {renderField('Secondary Number', partyContactsData.find(c => c.Type === 'Phone' && c.IsPrimary === 'No')?.Locator, false)}
          {renderField('Personal Email', employeeData.PrimaryContactEmail || employeeData.DGMA_PersonalEmail, false)}
        </div>
      </div>

      {/* Bank Accounts Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Bank Accounts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Account Type', bankAccountData?.AccountIdentification, false, 'BankAccountType')}
          {renderField('Name of Bank', bankAccountData?.Name, true, 'Name')}
          {renderField('Bank Account Number', bankAccountData?.BankAccountNumber, true, 'BankAccountNumber')}
          {renderField('Bank Account Holder Name', bankAccountData?.AccountHolder, true, 'AccountHolder')}
          {renderField('Sort Code', bankAccountData?.BranchNumber, true, 'BranchNumber')}
          {renderField('Bank Roll Numbers / Building Society Ref No', bankAccountData?.DGMA_BankRollNumber, false, 'DGMA_BankRollNumber')}
        </div>
      </div>

      {/* Protected Characteristics Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Protected Characteristics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Date of Birth', formatDate(employeeData.BirthDate), false)}
          {renderField('Ethnic Origin', employeeData.DGMA_EthnicOrigin, true, 'DGMA_EthnicOrigin')}
          {renderField('Sex', employeeData.SHPSex, true, 'SHPSex', 'select', ['Male', 'Female'])}
          {renderField('Disability (impairment, condition or access need)', employeeData.DGMA_Disability, true, 'DGMA_Disability', 'select', DGMA_DISABILITY_OPTIONS)}
          {renderField('Sexual Orientation', employeeData.DGMA_SexualOrientations, true, 'DGMA_SexualOrientations', 'select', DGMA_SEXUAL_ORIENTATIONS_OPTIONS)}
          {renderField('Religion', employeeData.DGMA_Religion, true, 'DGMA_Religion', 'select', DGMA_RELIGION_OPTIONS)}
          {renderField('Transgender', employeeData.DGMA_Transgender, true, 'DGMA_Transgender', 'select', DGMA_TRANSGENDER_OPTIONS)}
        </div>
      </div>

      {/* Languages Section - From API */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold text-gray-800">Languages</h3>
        </div>

        {languagesData && languagesData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {languagesData.map((lang, index) => (
              <div key={index} className="border border-gray-100 rounded-lg p-3 bg-gray-50 opacity-60">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Language</p>
                <p className="text-sm text-gray-800 font-medium">{lang.LanguageId || 'N/A'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No languages added yet.</p>
        )}
      </div>

      {/* Personal Contacts Section - Repeatable */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold text-gray-800">Emergency Contacts</h3>
          {isEditMode && (
            <button
              onClick={addContact}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add Contact
            </button>
          )}
        </div>
        {(isEditMode ? (editValues.PersonalContacts?.length > 0) : personalContactData.length > 0) ? (
          (isEditMode ? editValues.PersonalContacts : personalContactData).map((rawContact, contactIndex) => {
            const contact = !isEditMode ? {
              firstName: rawContact.ContactFirstName,
              middleName: rawContact.ContactMiddleName,
              lastName: rawContact.ContactLastName,
              relationship: rawContact.DGMA_RelationShipType,
              emergencyContactInfo: rawContact.IsEmergencyContact,
              primarySecondaryPhone: rawContact.EmergencyContactPrimarySecondary,
              addresses: [],
            } : rawContact;
            return (
            <div key={contactIndex} className="border border-gray-300 rounded-lg p-4 mb-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-medium text-gray-700">Contact #{contactIndex + 1}</h4>
                <div className="flex items-center gap-2">
                  {!isEditMode && rawContact.ContactPersonPartyNumber && (
                    <button
                      onClick={() => setContactToDelete({
                        isRemote: true,
                        apiContact: rawContact,
                        displayName: `${rawContact.ContactFirstName || ''} ${rawContact.ContactLastName || ''}`.trim() || 'this contact',
                      })}
                      className="text-red-500 hover:text-red-700"
                      title="Delete contact"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {isEditMode && (
                    <button
                      onClick={() => {
                        const isRemote = !!contact.contactPersonPartyNumber;
                        const apiContact = isRemote
                          ? (personalContactData ?? []).find(c => c.ContactPersonPartyNumber === contact.contactPersonPartyNumber)
                          : null;
                        setContactToDelete({
                          isRemote,
                          apiContact: apiContact || null,
                          localIndex: contactIndex,
                          displayName: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'this contact',
                        });
                      }}
                      disabled={saving}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      title="Delete contact"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">First Name</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={contact.firstName}
                      onChange={(e) => handleContactChange(contactIndex, 'firstName', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    />
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.firstName || 'N/A'}</p>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Middle Name</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={contact.middleName}
                      onChange={(e) => handleContactChange(contactIndex, 'middleName', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    />
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.middleName || 'N/A'}</p>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Last Name</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={contact.lastName}
                      onChange={(e) => handleContactChange(contactIndex, 'lastName', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    />
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.lastName || 'N/A'}</p>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Relationship</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={contact.relationship}
                      onChange={(e) => handleContactChange(contactIndex, 'relationship', e.target.value)}
                      placeholder="e.g., Spouse, Parent, Sibling"
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    />
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.relationship || 'N/A'}</p>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Emergency Contact</p>
                  {isEditMode ? (
                    <select
                      value={contact.emergencyContactInfo || ''}
                      onChange={(e) => handleContactChange(contactIndex, 'emergencyContactInfo', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.emergencyContactInfo || 'N/A'}</p>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg p-3 bg-white">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Primary/Secondary</p>
                  {isEditMode ? (
                    <select
                      value={contact.primarySecondaryPhone || ''}
                      onChange={(e) => handleContactChange(contactIndex, 'primarySecondaryPhone', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={saving}
                    >
                      <option value="">Select...</option>
                      <option value="Primary">Primary</option>
                      <option value="Secondary">Secondary</option>
                    </select>
                  ) : (
                    <p className="text-sm text-gray-800 font-medium">{contact.primarySecondaryPhone || 'N/A'}</p>
                  )}
                </div>
              </div>

              {/* Contact Addresses (edit mode) - local address form for each contact */}
              {isEditMode && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-semibold text-gray-700">Contact Addresses</h5>
                    {(contact.addresses || []).length === 0 && (
                      <button
                        onClick={() => addContactAddress(contactIndex)}
                        disabled={saving}
                        className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <Plus className="w-3 h-3" />
                        Add Address
                      </button>
                    )}
                  </div>
                  {(contact.addresses || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">No addresses added. Click "Add Address" to add one.</p>
                  ) : (
                    (contact.addresses || []).map((addr, addrIdx) => (
                      <div key={addrIdx} className="border border-gray-200 rounded-lg p-3 bg-white mb-2">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-gray-600">Address #{addrIdx + 1}</span>
                          <button
                            onClick={() => removeContactAddress(contactIndex, addrIdx)}
                            disabled={saving}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {[
                            { label: 'Name or Description', field: 'name' },
                            { label: 'Street', field: 'street' },
                            { label: 'City', field: 'city' },
                            { label: 'Country', field: 'country', type: 'dropdown' },
                            { label: 'Zip Code', field: 'zipCode' },
                            { label: 'Contact Number', field: 'contactNumber' },
                          ].map(({ label, field, type }) => (
                            <div key={field}>
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
                              {type === 'dropdown' ? (
                                <select
                                  value={addr[field] || ''}
                                  onChange={(e) => handleContactAddressChange(contactIndex, addrIdx, field, e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                  disabled={saving}
                                >
                                  <option value="">Select Country</option>
                                  {countryOptions.map((country) => (
                                    <option key={country} value={country}>{country}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={addr[field] || ''}
                                  onChange={(e) => handleContactAddressChange(contactIndex, addrIdx, field, e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  disabled={saving}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Contact Addresses - from PartyLocationPostalAddresses (view mode) */}
              {!isEditMode && (
                <div className="mt-4">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Contact Addresses</h5>
                  {isLoadingContactAddresses ? (
                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[...Array(6)].map((_, i) => (
                          <div key={i}>
                            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (() => {
                    const contactAddr = postalAddressesMap[rawContact.ContactPersonPartyNumber];
                    if (!contactAddr) return null;
                    return (
                      <div className="border border-gray-200 rounded-lg p-3 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[
                            { label: 'Name or Description', display: contactAddr.Description },
                            { label: 'Street', display: contactAddr.Street },
                            { label: 'City', display: contactAddr.City },
                            { label: 'Zip Code', display: contactAddr.ZipCode },
                            { label: 'Country', display: contactAddr.CountryRegionId },
                          ].map(({ label, display }) => (
                            <div key={label}>
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
                              <p className="text-xs text-gray-800">{display || 'N/A'}</p>
                            </div>
                          ))}
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Contact Number</p>
                            <p className="text-xs text-gray-800">{contactPartyContactsMap[rawContact.ContactPersonPartyNumber]?.Locator || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Roles</p>
                            <p className="text-xs text-gray-800">{contactAddr.Roles || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            );
          })
        ) : (
          <p className="text-sm text-gray-500 italic">No emergency contacts added yet.</p>
        )}
      </div>

      {/* Delete Confirmation Modal — rendered via portal to avoid overflow clipping */}
      {contactToDelete && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Contact</h3>
                <p className="text-sm text-gray-500">
                  {contactToDelete.isRemote ? 'This action cannot be undone.' : 'This will remove the unsaved contact.'}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-medium">{contactToDelete.displayName}</span>?
            </p>
            {contactToDelete.isRemote && (
              <p className="text-xs text-gray-500 mb-6">
                This will permanently delete the personal contact and all associated postal addresses.
              </p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setContactToDelete(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteContact}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Employment Details Component
const CONTRACT_DURATION_MAP = {
  OneMonth: '1 Month',
  TwoMonths: '2 Months',
  ThreeMonths: '3 Months',
  FourMonths: '4 Months',
  FiveMonths: '5 Months',
  SixMonths: '6 Months',
  SevenMonths: '7 Months',
  EightMonths: '8 Months',
  NineMonths: '9 Months',
  TenMonths: '10 Months',
  ElevenMonths: '11 Months',
  OneYear: '1 Year',
  OneAndHalfYear: '1.5 Years',
  TwoYears: '2 Years',
  TwoAndHalfYears: '2.5 Years',
  ThreeYears: '3 Years',
};

const EmploymentDetails = ({ employeeData, positionsData = [] }) => {
  if (!employeeData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="ml-3 text-gray-600">Loading employment details...</p>
        </div>
      </div>
    );
  }

  const renderField = (label, value) => (
    <div className="border border-gray-100 rounded-lg p-3">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className="text-sm text-gray-800 font-medium">{value || 'N/A'}</p>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1900-01-01T00:00:00Z' || dateStr === '1900-01-01T12:00:00Z') return 'N/A';
    const date = new Date(dateStr);
    if (date.getFullYear() >= 2100) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="p-6">
      {/* Employment Summary Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-indigo-600" />
          Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Employment Start Date', formatDate(employeeData.EmploymentStartDate))}
          {renderField('Continuous Service', formatDate(employeeData.AdjustedWorkerStartDate))}
          {renderField('Probation Period', employeeData.DGMA_EmploymentProbationPeriod)}
          {renderField('Extended Probation Date', formatDate(employeeData.DGMA_ExtendedProbationDate))}
        </div>
      </div>

      {/* Work History Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Job Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Employment Contract Status', employeeData.DGMA_EmploymentContractStatus?.replace(/([A-Z])/g, '-$1').replace(/^-/, ''))}
          {renderField('Contract Duration', CONTRACT_DURATION_MAP[employeeData.DGMA_EmploymentContractDuration] || formatDate(employeeData.DGMA_EmploymentContractDuration))}
          {renderField('Weekly Hours for Perm/Fixed term Staff', employeeData.DGMA_WeeklyHoursForPermFixedTermStaff)}
          {renderField('Job Title', positionsData[0]?.Description)}
          {renderField('Job Start Date', formatDate(positionsData[0]?.WorkerAssignmentStart))}
          {renderField('Job End Date', formatDate(positionsData[0]?.WorkerAssignmentEnd))}
          {renderField('Terms of Employment', employeeData.DGMA_TermsOfEmployment)}
        </div>
      </div>

    </div>
  );
};

// Other Details Component
const OtherDetails = ({ employeeData, operatingUnitName, onUpdate }) => {
  if (!employeeData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="ml-3 text-gray-600">Loading other details...</p>
        </div>
      </div>
    );
  }

  const renderField = (label, value) => (
    <div className="border border-gray-100 rounded-lg p-3">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className="text-sm text-gray-800 font-medium">{value || 'N/A'}</p>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '1900-01-01T00:00:00Z' || dateStr === '1900-01-01T12:00:00Z') return 'N/A';
    const date = new Date(dateStr);
    if (date.getFullYear() >= 2100) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-600" />
          Other Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderField('Cost Center and Site Code', employeeData.DGMA_CostCenterAndSiteCode)}
          {renderField('Site', operatingUnitName || employeeData.DGMA_Site)}
          {renderField('National Insurance No.', employeeData.NINO)}
        </div>
      </div>
    </div>
  );
};

const PersonalInformation = ({ hideHeader = false, title, subtitle }) => {
  const { setHeader } = usePageHeader();
  const userProfile = useEmployeeProfile();
  const [activeTab, setActiveTab] = useState('personal');
  const [employeeData, setEmployeeData] = useState(null);
  const [languagesData, setLanguagesData] = useState([]);
  const [bankAccountData, setBankAccountData] = useState(null);
  const [personalContactData, setPersonalContactData] = useState(null);
  const [postalAddressData, setPostalAddressData] = useState(null);
  const [positionsData, setPositionsData] = useState([]);
  const [partyContactsData, setPartyContactsData] = useState([]);
  const [contactPartyContactsMap, setContactPartyContactsMap] = useState({});
  const [isLoadingContactAddresses, setIsLoadingContactAddresses] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [operatingUnitsData, setOperatingUnitsData] = useState([]);
  const [operatingUnitName, setOperatingUnitName] = useState(null);

  useEffect(() => {
    if (!hideHeader) {
      setHeader({
        icon: User,
        title: title || 'Personal Information',
        subtitle: subtitle || 'View and update your personal and employment information.',
      });
      return () => setHeader({ icon: null, title: '', subtitle: '' });
    }
  }, [hideHeader, title, subtitle, setHeader]);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!userProfile?.employeeNo) {
        console.log('⏳ Waiting for employee number...');
        return;
      }

      console.log('👤 Fetching employee data for:', userProfile.employeeNo);
      setIsLoadingData(true);
      try {
        const response = await employeeService.getEmployeeByNumber(userProfile.employeeNo);
        const employee = response.data.value?.[0];
        console.log('✅ Employee data fetched:', employee ? 'Found' : 'Not found');
        setEmployeeData(employee || null);
      } catch (error) {
        console.error('❌ Failed to fetch employee data:', error);
        setEmployeeData(null);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchEmployeeData();
    }
  }, [userProfile.isLoading, userProfile.employeeNo]);

  // Fetch languages data
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!userProfile?.employeeNo) return;

      console.log('🌐 Fetching languages for:', userProfile.employeeNo);
      try {
        const response = await languageService.getLanguages(userProfile.employeeNo);
        const languages = response.data?.value || [];
        console.log('✅ Languages API Response:', JSON.stringify(languages, null, 2));
        setLanguagesData(languages);
      } catch (error) {
        console.error('❌ Failed to fetch languages:', error);
        setLanguagesData([]);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchLanguages();
    }
  }, [userProfile.isLoading, userProfile.employeeNo]);

  // Fetch worker bank accounts
  useEffect(() => {
    const fetchWorkerBankAccounts = async () => {
      if (!userProfile?.employeeNo) return;

      console.log('🏦 Fetching WorkerBankAccounts for:', userProfile.employeeNo);
      try {
        const response = await employeeService.getWorkerBankAccounts(userProfile.employeeNo);
        const account = response.data?.value?.[0] || null;
        console.log('✅ WorkerBankAccounts API Response:', JSON.stringify(account, null, 2));
        setBankAccountData(account);
      } catch (error) {
        console.error('❌ Failed to fetch WorkerBankAccounts:', error);
        setBankAccountData(null);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchWorkerBankAccounts();
    }
  }, [userProfile.isLoading, userProfile.employeeNo]);

  // Fetch personal contact people
  useEffect(() => {
    const fetchPersonalContactPeople = async () => {
      console.log('👥 Fetching PersonalContactPeople');
      try {
        const response = await employeeService.getPersonalContactPeople(userProfile.employeeNo);
        const contacts = response.data?.value || [];
        console.log('✅ PersonalContactPeople API Response:', JSON.stringify(contacts, null, 2));
        setPersonalContactData(contacts);
      } catch (error) {
        console.error('❌ Failed to fetch PersonalContactPeople:', error);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchPersonalContactPeople();
    }
  }, [userProfile.isLoading, userProfile.employeeNo]);

  // Fetch PartyContactsV3 for each personal contact (phone numbers)
  useEffect(() => {
    const fetchContactPartyContacts = async () => {
      if (!personalContactData?.length) return;
      setIsLoadingContactAddresses(true);
      const map = {};
      await Promise.all(
        personalContactData.map(async (contact) => {
          if (!contact.ContactPersonPartyNumber) return;
          try {
            const response = await partyContactService.getPartyContacts(contact.ContactPersonPartyNumber);
            const entry = response.data?.value?.[0] || null;
            if (entry) map[contact.ContactPersonPartyNumber] = entry;
          } catch (e) {
            console.error('❌ Failed to fetch PartyContactsV3 for', contact.ContactPersonPartyNumber, e);
          }
        })
      );
      setContactPartyContactsMap(map);
      setIsLoadingContactAddresses(false);
    };

    fetchContactPartyContacts();
  }, [personalContactData]);

  // Fetch party contacts
  useEffect(() => {
    const fetchPartyContacts = async () => {
      if (!employeeData?.PartyNumber) return;

      console.log('📇 Fetching PartyContactsV3 for PartyNumber:', employeeData.PartyNumber);
      try {
        const response = await partyContactService.getPartyContacts(employeeData.PartyNumber);
        const contacts = response.data?.value || [];
        console.log('✅ PartyContactsV3 API Response:', JSON.stringify(contacts, null, 2));
        setPartyContactsData(contacts);
      } catch (error) {
        console.error('❌ Failed to fetch PartyContactsV3:', error);
        setPartyContactsData([]);
      }
    };

    fetchPartyContacts();
  }, [employeeData?.PartyNumber]);

  // Fetch positions by worker
  useEffect(() => {
    const fetchPositions = async () => {
      if (!userProfile?.employeeNo) return;

      console.log('📋 Fetching PositionsV2 for:', userProfile.employeeNo);
      try {
        const response = await positionService.getPositionsByWorker(userProfile.employeeNo);
        const positions = response.data?.value || [];
        console.log('✅ PositionsV2 API Response:', JSON.stringify(positions, null, 2));
        setPositionsData(positions);
      } catch (error) {
        console.error('❌ Failed to fetch positions:', error);
        setPositionsData([]);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchPositions();
    }
  }, [userProfile.isLoading, userProfile.employeeNo]);

  // Fetch party location postal addresses for all contacts
  useEffect(() => {
    const fetchAllPostalAddresses = async () => {
      if (!personalContactData?.length) return;
      setIsLoadingContactAddresses(true);
      const map = {};
      await Promise.all(
        personalContactData.map(async (contact) => {
          if (!contact.ContactPersonPartyNumber) return;
          try {
            const response = await employeeService.getPartyLocationPostalAddresses(contact.ContactPersonPartyNumber);
            const address = response.data?.value?.[0] || null;
            if (address) map[contact.ContactPersonPartyNumber] = address;
          } catch (e) {
            console.error('❌ Failed to fetch address for', contact.ContactPersonPartyNumber, e);
          }
        })
      );
      setPostalAddressData(map);
      setIsLoadingContactAddresses(false);
    };

    fetchAllPostalAddresses();
  }, [personalContactData]);

  // Fetch country options
  useEffect(() => {
    const fetchCountryOptions = async () => {
      try {
        const response = await employeeService.getAddressCountryRegions();
        const countries = response.data?.value || [];
        const options = countries.map(c => c.CountryRegion).filter(Boolean).sort();
        setCountryOptions(options);
      } catch (error) {
        console.error('❌ Failed to fetch country options:', error);
      }
    };
    fetchCountryOptions();
  }, []);

  // Fetch Operating Units filtered by Site value
  useEffect(() => {
    const fetchOperatingUnits = async () => {
      if (!employeeData?.DGMA_Site) {
        return;
      }

      const siteValue = employeeData.DGMA_Site;

      try {
        const params = {
          $filter: `OperatingUnitNumber eq '${siteValue}'`
        };

        const response = await organizationService.getOperatingUnits(params);
        const units = response.data?.value || [];

        if (units.length > 0) {
          setOperatingUnitName(units[0].Name);
        } else {
          setOperatingUnitName(null);
        }

        setOperatingUnitsData(units);
      } catch (error) {
        setOperatingUnitsData([]);
      }
    };
    fetchOperatingUnits();
  }, [employeeData?.DGMA_Site]);

  const tabs = [
    { id: 'personal', label: 'Personal Details', icon: User },
    { id: 'employment', label: 'Employment Details', icon: Briefcase },
    { id: 'other', label: 'Other Details', icon: FileText },
  ];

  const content = (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6">
        <nav className="-mb-px flex space-x-2 sm:space-x-4 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 sm:py-3 px-2 sm:px-4 border-b-2 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 ${
                  isActive
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'personal' && (
        <PersonalDetailsView
          employeeData={employeeData}
          languagesData={languagesData}
          bankAccountData={bankAccountData}
          onBankAccountUpdate={(updated) => setBankAccountData(updated)}
          personalContactData={personalContactData ?? []}
          onPersonalContactUpdate={(updated) => setPersonalContactData(updated)}
          postalAddressesMap={postalAddressData ?? {}}
          partyContactsData={partyContactsData}
          contactPartyContactsMap={contactPartyContactsMap}
          isLoadingContactAddresses={isLoadingContactAddresses}
          countryOptions={countryOptions}
          onUpdate={(updatedData) => setEmployeeData(updatedData)}
        />
      )}
      {activeTab === 'employment' && <EmploymentDetails employeeData={employeeData} positionsData={positionsData} />}
      {activeTab === 'other' && (
        <OtherDetails
          employeeData={employeeData}
          operatingUnitName={operatingUnitName}
          onUpdate={(updatedData) => setEmployeeData(updatedData)}
        />
      )}
    </div>
  );

  if (hideHeader) {
    return content;
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {content}
    </div>
  );
};

export default PersonalInformation;
